import axios from 'axios'

const API_KEY = '6517f2df219c391b08726816cd3f4feb'
const BASE_URL = 'https://api.themoviedb.org/3'

// Enhanced content filtering parameters
const CONTENT_SAFETY_PARAMS = {
  include_adult: false,
  certification_country: 'US',
  'certification.lte': 'PG-13', // Only G, PG, and PG-13 rated movies
  language: 'en-US'
}

// Create axios instance with enhanced default config
const tmdbApi = axios.create({
  baseURL: BASE_URL,
  params: {
    api_key: API_KEY,
    ...CONTENT_SAFETY_PARAMS
  }
})

// Comprehensive adult content filtering function (enhanced)
const filterAdultContent = (movies) => {
  return movies.filter(movie => {
    // Primary adult filter
    if (movie.adult === true) return false
    
    // Enhanced genre filtering - exclude potentially problematic genres
    const excludedGenres = [
      10770, // TV Movie (can contain adult content)
      99,    // Documentary (might contain mature themes)
      36     // History (might contain war/violence themes)
    ]
    if (movie.genre_ids?.some(id => excludedGenres.includes(id))) return false
    
    // Expanded keyword filtering for inappropriate content
    const inappropriateKeywords = [
      // Explicit content keywords
      'xxx', 'porn', 'erotic', 'seduction', 'lust', 'desire', 'temptation',
      'intimate', 'sensual', 'passion', 'forbidden', 'affair', 'mistress',
      'stepmom', 'stepson', 'stepdaughter', 'stepfather', 'rape',
      'golden lotus', 'dead 3', 'emmanuelle', 'debbie does', 'naughty',
      'sins of', 'confessions', 'secret desires', 'fifty shades',
      'explicit', 'uncensored', 'uncut', 'director\'s cut', 'extended cut',
      
      // Violence and mature themes
      'massacre', 'slaughter', 'torture', 'gore', 'brutal', 'savage',
      'psychopath', 'serial killer', 'murder', 'bloodbath', 'carnage',
      
      // Drug and substance abuse
      'cocaine', 'heroin', 'meth', 'drugs', 'addiction', 'overdose',
      'drug dealer', 'cartel', 'trafficking',
      
      // Disturbing themes
      'cult', 'satanic', 'demon', 'possessed', 'exorcism', 'occult',
      'suicide', 'self-harm', 'depression', 'mental illness'
    ]
    
    const title = movie.title?.toLowerCase() || ''
    const overview = movie.overview?.toLowerCase() || ''
    
    for (const keyword of inappropriateKeywords) {
      if (title.includes(keyword) || overview.includes(keyword)) {
        return false
      }
    }
    
    // Enhanced rating filters
    if (movie.vote_average === 0 && movie.vote_count < 100) return false
    
    // Filter out movies with very low ratings (might indicate poor quality)
    if (movie.vote_average < 3.0 && movie.vote_count > 50) return false
    
    // Additional safety patterns
    const suspiciousPatterns = [
      /\b\d+[\s-]*\d+[\s-]*\d+\b/, // Phone number patterns
      /\bxxx\b/i,
      /\bnsfw\b/i,
      /\br-rated\b/i,
      /\bunrated\b/i,
      /\badult\s+film\b/i
    ]
    
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(title) || pattern.test(overview)) {
        return false
      }
    }
    
    return true
  })
}

// Enhanced parameter building function
const buildSafeParams = (additionalParams = {}) => {
  return {
    ...CONTENT_SAFETY_PARAMS,
    ...additionalParams
  }
}

// Get popular movies with enhanced certification filtering
export async function getPopularMovies(page = 1) {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        sort_by: 'popularity.desc',
        'vote_count.gte': 100 // Ensure quality content
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching popular movies:', error)
    throw new Error('Failed to fetch popular movies')
  }
}

// Get trending movies with certification filtering
export async function getTrendingMovies(timeWindow = 'week', page = 1) {
  try {
    // Note: trending endpoint doesn't support certification params, so we use discover with trending-like sorting
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        sort_by: 'popularity.desc',
        'primary_release_date.gte': getDateWeeksAgo(timeWindow === 'week' ? 1 : 4),
        'vote_count.gte': 50
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching trending movies:', error)
    throw new Error('Failed to fetch trending movies')
  }
}

// Get top rated movies with certification filtering
export async function getTopRatedMovies(page = 1) {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        sort_by: 'vote_average.desc',
        'vote_count.gte': 300, // Higher threshold for top rated
        'vote_average.gte': 7.0
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching top rated movies:', error)
    throw new Error('Failed to fetch top rated movies')
  }
}

// Get upcoming movies with certification filtering
export async function getUpcomingMovies(page = 1) {
  try {
    const today = new Date().toISOString().split('T')[0]
    const futureDate = new Date()
    futureDate.setMonth(futureDate.getMonth() + 6)
    
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        'primary_release_date.gte': today,
        'primary_release_date.lte': futureDate.toISOString().split('T')[0],
        sort_by: 'primary_release_date.asc'
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching upcoming movies:', error)
    throw new Error('Failed to fetch upcoming movies')
  }
}

// Discover movies by genre with enhanced filtering
export async function discoverMoviesByGenre(genreId, page = 1, sortBy = 'popularity.desc') {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        with_genres: genreId,
        sort_by: sortBy,
        'vote_count.gte': 100
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error discovering movies:', error)
    throw new Error('Failed to discover movies')
  }
}

// Enhanced search movies with certification filtering
export async function searchMovies(query, page = 1) {
  try {
    if (!query || query.trim() === '') {
      return getPopularMovies(page)
    }

    // First try search endpoint (doesn't support certification)
    let response = await axios.get(`${BASE_URL}/search/movie`, {
      params: {
        api_key: API_KEY,
        query: encodeURIComponent(query.trim()),
        page,
        include_adult: false,
        language: 'en-US'
      }
    })
    
    let filteredMovies = filterAdultContent(response.data.results)
    
    // If search results are insufficient, supplement with discover
    if (filteredMovies.length < 5 && page === 1) {
      try {
        const discoverResponse = await tmdbApi.get('/discover/movie', {
          params: buildSafeParams({
            page,
            sort_by: 'popularity.desc',
            with_keywords: await getKeywordId(query)
          })
        })
        
        const discoverFiltered = filterAdultContent(discoverResponse.data.results)
        filteredMovies = [...filteredMovies, ...discoverFiltered]
          .filter((movie, index, self) => 
            index === self.findIndex(m => m.id === movie.id)
          )
          .slice(0, 20)
      } catch (discoverError) {
        console.warn('Discover fallback failed:', discoverError)
      }
    }
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results,
      searchQuery: query
    }
  } catch (error) {
    console.error('Error searching movies:', error)
    throw new Error('Failed to search movies')
  }
}

// Get movie details with enhanced safety checks
export async function getMovieDetails(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}`, {
      params: {
        api_key: API_KEY,
        language: 'en-US'
      }
    })
    
    const movie = response.data
    
    // Enhanced safety checks
    if (movie.adult === true) {
      throw new Error('Content not available')
    }
    
    // Check certification
    const releases = await getMovieReleases(id)
    const usRelease = releases.find(release => release.iso_3166_1 === 'US')
    if (usRelease && usRelease.release_dates?.length > 0) {
      const certification = usRelease.release_dates[0].certification
      const allowedRatings = ['G', 'PG', 'PG-13', '']
      if (certification && !allowedRatings.includes(certification)) {
        throw new Error('Content not available')
      }
    }
    
    // Additional content filtering
    const filteredMovie = filterAdultContent([movie])
    if (filteredMovie.length === 0) {
      throw new Error('Content not available')
    }
    
    return movie
  } catch (error) {
    console.error('Error fetching movie details:', error)
    
    if (error.response?.status === 404) {
      throw new Error('Movie not found')
    }
    
    if (error.message === 'Content not available') {
      throw new Error('Content not available')
    }
    
    throw new Error('Failed to fetch movie details')
  }
}

// Get movie releases (for certification checking)
async function getMovieReleases(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}/release_dates`, {
      params: {
        api_key: API_KEY
      }
    })
    return response.data.results
  } catch (error) {
    console.warn('Failed to fetch movie releases:', error)
    return []
  }
}

// Get movie videos with content filtering
export async function getMovieVideos(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}/videos`, {
      params: {
        api_key: API_KEY,
        language: 'en-US'
      }
    })
    
    return response.data.results.filter(video => {
      const allowedTypes = ['Trailer', 'Teaser', 'Clip', 'Behind the Scenes', 'Featurette']
      const allowedSites = ['YouTube']
      
      return allowedTypes.includes(video.type) && 
             allowedSites.includes(video.site) &&
             !video.name?.toLowerCase().includes('red band')
    })
  } catch (error) {
    console.error('Error fetching movie videos:', error)
    return []
  }
}

// Get similar movies with certification filtering
export async function getSimilarMovies(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}/similar`, {
      params: {
        api_key: API_KEY,
        language: 'en-US',
        include_adult: false
      }
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    return filteredMovies.slice(0, 12)
  } catch (error) {
    console.error('Error fetching similar movies:', error)
    return []
  }
}

// Get movie recommendations with certification filtering
export async function getMovieRecommendations(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}/recommendations`, {
      params: {
        api_key: API_KEY,
        language: 'en-US',
        include_adult: false
      }
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    return filteredMovies.slice(0, 12)
  } catch (error) {
    console.error('Error fetching movie recommendations:', error)
    return []
  }
}

// Get movie genres with filtering
export async function getMovieGenres() {
  try {
    const response = await axios.get(`${BASE_URL}/genre/movie/list`, {
      params: {
        api_key: API_KEY,
        language: 'en-US'
      }
    })
    
    // Filter out potentially problematic genres
    const excludedGenres = ['Horror', 'Thriller', 'War', 'Crime']
    const safeGenres = response.data.genres.filter(genre => 
      !excludedGenres.includes(genre.name)
    )
    
    return safeGenres
  } catch (error) {
    console.error('Error fetching genres:', error)
    return []
  }
}

// Get movie cast and crew with filtering
export async function getMovieCredits(id) {
  try {
    const response = await axios.get(`${BASE_URL}/movie/${id}/credits`, {
      params: {
        api_key: API_KEY,
        language: 'en-US'
      }
    })
    
    return {
      cast: response.data.cast.slice(0, 20),
      crew: response.data.crew.filter(person => 
        ['Director', 'Producer', 'Executive Producer', 'Writer', 'Screenplay', 'Story', 'Director of Photography', 'Original Music Composer'].includes(person.job)
      )
    }
  } catch (error) {
    console.error('Error fetching movie credits:', error)
    return { cast: [], crew: [] }
  }
}

// Get movies by rating range with certification filtering
export async function getMoviesByRating(minRating = 7, maxRating = 10, page = 1) {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        'vote_average.gte': minRating,
        'vote_average.lte': maxRating,
        'vote_count.gte': 500,
        sort_by: 'vote_average.desc'
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching movies by rating:', error)
    throw new Error('Failed to fetch movies by rating')
  }
}

// Get now playing movies with certification filtering
export async function getNowPlayingMovies(page = 1) {
  try {
    const today = new Date().toISOString().split('T')[0]
    const pastDate = new Date()
    pastDate.setMonth(pastDate.getMonth() - 2)
    
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        'primary_release_date.gte': pastDate.toISOString().split('T')[0],
        'primary_release_date.lte': today,
        sort_by: 'primary_release_date.desc'
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching now playing movies:', error)
    throw new Error('Failed to fetch now playing movies')
  }
}

// Helper function to get date weeks ago
function getDateWeeksAgo(weeks) {
  const date = new Date()
  date.setDate(date.getDate() - (weeks * 7))
  return date.toISOString().split('T')[0]
}

// Helper function to get keyword ID (simplified version)
async function getKeywordId(query) {
  try {
    const response = await axios.get(`${BASE_URL}/search/keyword`, {
      params: {
        api_key: API_KEY,
        query: query
      }
    })
    return response.data.results[0]?.id || null
  } catch (error) {
    return null
  }
}

// Family-friendly movie discovery
export async function getFamilyFriendlyMovies(page = 1) {
  try {
    const response = await tmdbApi.get('/discover/movie', {
      params: buildSafeParams({
        page,
        'certification.lte': 'PG', // Only G and PG rated
        with_genres: '16,10751,35,12', // Animation, Family, Comedy, Adventure
        sort_by: 'popularity.desc',
        'vote_count.gte': 100
      })
    })
    
    const filteredMovies = filterAdultContent(response.data.results)
    
    return {
      movies: filteredMovies,
      totalPages: Math.min(response.data.total_pages, 500),
      currentPage: page,
      totalResults: response.data.total_results
    }
  } catch (error) {
    console.error('Error fetching family-friendly movies:', error)
    throw new Error('Failed to fetch family-friendly movies')
  }
}

// Utility functions (same as before but with enhanced safety)
export function getImageUrl(path, size = 'w500') {
  if (!path) return null
  return `https://image.tmdb.org/t/p/${size}${path}`
}

export function formatRuntime(minutes) {
  if (!minutes) return 'N/A'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
}

export function formatMoney(amount) {
  if (!amount) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatDate(dateString) {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}